import type { Mistake } from '../../../lib/api';
import { getSkillSectionLabel } from '@examflow/core';

export type ExamTypeFilter = string | null;
export type SectionFilter = string | null;
export type DifficultyFilter = string | null;
export type SortField = 'lastWrongTime' | 'wrongCount' | 'difficulty' | 'nextReviewTime';
export type SortDirection = 'asc' | 'desc';

export interface MistakeFilters {
  examType: ExamTypeFilter;
  section: SectionFilter;
  difficulty: DifficultyFilter;
  sortBy: SortField;
  sortDirection: SortDirection;
}

export interface MistakeGroup {
  key: string;
  label: string;
  count: number;
  mistakes: Mistake[];
}

export interface MistakeFilterStats {
  totalMistakes: number;
  byExamType: Map<string, number>;
  bySection: Map<string, number>;
  byDifficulty: Map<string, number>;
  byWrongCount: Map<number, number>;
}

export const DEFAULT_FILTERS: MistakeFilters = {
  examType: null,
  section: null,
  difficulty: null,
  sortBy: 'lastWrongTime',
  sortDirection: 'desc',
};

export function getSectionLabel(section: string | null | undefined): string {
  if (!section) return '未知';
  return getSkillSectionLabel(section);
}

export function getDifficultyLabel(difficulty: string | null | undefined): string {
  if (!difficulty) return '未知';
  return difficulty;
}

/**
 * Group mistakes by a specific field
 */
export function groupMistakesByField(
  mistakes: Mistake[],
  field: 'examType' | 'section' | 'difficulty'
): MistakeGroup[] {
  const groups = new Map<string, Mistake[]>();

  for (const mistake of mistakes) {
    let key: string;
    switch (field) {
      case 'examType':
        key = mistake.question.examType ?? 'unknown';
        break;
      case 'section':
        key = mistake.question.section ?? 'unknown';
        break;
      case 'difficulty':
        key = mistake.question.level ?? 'unknown';
        break;
      default:
        key = 'unknown';
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(mistake);
  }

  return Array.from(groups.entries())
    .map(([key, groupMistakes]) => ({
      key,
      label:
        field === 'section'
          ? getSectionLabel(key)
          : field === 'difficulty'
            ? getDifficultyLabel(key)
            : key,
      count: groupMistakes.length,
      mistakes: groupMistakes,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Calculate statistics for a list of mistakes
 */
export function calculateMistakeStats(mistakes: Mistake[]): MistakeFilterStats {
  const stats: MistakeFilterStats = {
    totalMistakes: mistakes.length,
    byExamType: new Map(),
    bySection: new Map(),
    byDifficulty: new Map(),
    byWrongCount: new Map(),
  };

  for (const mistake of mistakes) {
    // By exam type
    const examType = mistake.question.examType ?? 'unknown';
    stats.byExamType.set(examType, (stats.byExamType.get(examType) ?? 0) + 1);

    // By section
    const section = mistake.question.section ?? 'unknown';
    stats.bySection.set(section, (stats.bySection.get(section) ?? 0) + 1);

    // By difficulty
    const difficulty = mistake.question.level ?? 'unknown';
    stats.byDifficulty.set(difficulty, (stats.byDifficulty.get(difficulty) ?? 0) + 1);

    // By wrong count
    stats.byWrongCount.set(
      mistake.wrongCount,
      (stats.byWrongCount.get(mistake.wrongCount) ?? 0) + 1
    );
  }

  return stats;
}

/**
 * Apply filters to a list of mistakes
 */
export function applyMistakeFilters(mistakes: Mistake[], filters: MistakeFilters): Mistake[] {
  let filtered = [...mistakes];

  // Filter by exam type
  if (filters.examType) {
    filtered = filtered.filter((m) => m.question.examType === filters.examType);
  }

  // Filter by section
  if (filters.section) {
    filtered = filtered.filter((m) => m.question.section === filters.section);
  }

  // Filter by difficulty
  if (filters.difficulty) {
    filtered = filtered.filter((m) => m.question.level === filters.difficulty);
  }

  // Sort
  filtered.sort((a, b) => {
    let comparison = 0;
    switch (filters.sortBy) {
      case 'lastWrongTime':
        comparison = new Date(a.lastWrongTime).getTime() - new Date(b.lastWrongTime).getTime();
        break;
      case 'wrongCount':
        comparison = a.wrongCount - b.wrongCount;
        break;
      case 'difficulty': {
        const difficultyOrder = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
        const aIndex = difficultyOrder.indexOf(a.question.level ?? '');
        const bIndex = difficultyOrder.indexOf(b.question.level ?? '');
        comparison = aIndex - bIndex;
        break;
      }
      case 'nextReviewTime': {
        const aTime = a.nextReviewTime ? new Date(a.nextReviewTime).getTime() : Infinity;
        const bTime = b.nextReviewTime ? new Date(b.nextReviewTime).getTime() : Infinity;
        comparison = aTime - bTime;
        break;
      }
    }
    return filters.sortDirection === 'desc' ? -comparison : comparison;
  });

  return filtered;
}

/**
 * Get unique values for filter options with counts
 */
export function getFilterOptions(
  mistakes: Mistake[],
  field: 'examType' | 'section' | 'difficulty'
): Array<{ value: string; label: string; count: number }> {
  const groups = groupMistakesByField(mistakes, field);
  return groups.map((g) => ({
    value: g.key,
    label: g.label,
    count: g.count,
  }));
}
